/**
 * quick-view-price.js
 *
 * Fake Prices logic for Quick View modals in the new theme.
 *
 * How it works:
 * 1. The new theme uses a <quick-view> custom element.
 *    When the quick-view button is clicked, `quickview()` fetches
 *    `{productUrl}?view=modal` (which renders main-product-modal.liquid,
 *    which calls product-price.liquid, which renders our fake price HTML).
 *    After injecting the content via setInnerHTML, the element dispatches
 *    `quickview:loaded` on document.
 *
 * 2. We listen for `quickview:loaded` and apply fake price discounts
 *    to the modal content, scoped to `.quick-view__content`.
 *
 * 3. When the user changes a variant inside the modal, the product-info
 *    component (same as on PDP) does an SSR refresh of the Price block
 *    and publishes `theme.pubsub.PUB_SUB_EVENTS.variantChange`.
 *    We subscribe to this event and re-apply discounts after the refresh.
 *
 * 4. Scoping to `.quick-view__content` ensures we never accidentally
 *    touch the PDP or collection-card prices.
 *
 * Loaded on ALL pages when settings.show-fake-compare-price == true.
 */

function parseMoneyQV(text) {
  return parseFloat((text || '').replace(/[^\d.,]/g, '').replace(',', '.')) || 0;
}

function calcDiscountedQV(base, amount, type) {
  if (type === 'percentage') return Math.round(base * (1 - amount / 100));
  return Math.round(base - amount);
}

function formatFakeMoneyQV(amount) {
  return theme.Currency.formatMoneyText(Math.round(amount * 100), theme.settings.moneyFormat);
}

/**
 * Returns the content container of the currently open quick-view modal,
 * or null if no modal is open / no content has been loaded yet.
 */
function getOpenModalContainer() {
  const containers = document.querySelectorAll('quick-view .quick-view__content');
  for (const c of containers) {
    if (c.children.length > 0) return c;
  }
  return null;
}

/**
 * Applies fake price discounts to all matching elements inside `container`.
 * Reads the base price from `#first-price .fake-compare-price .money` (rendered by Liquid
 * with the variant's actual price). Reads discount config from
 * `.placeholder-for-cmc` (also rendered by product-price.liquid inside
 * the modal). Writes the calculated result into `#final-price-* .money`.
 */
function applyFakePricesInModal(container) {
  const firstPriceDelMoney = container.querySelector('#first-price .fake-compare-price .money');
  if (!firstPriceDelMoney) return;

  const basePrice = parseMoneyQV(firstPriceDelMoney.textContent);
  if (!basePrice) return;

  const get = id => parseFloat(container.querySelector('#' + id)?.textContent || 0);

  const discounts = {
    'final-price':                         { amount: get('discount-amount'),        type: 'amount'      },
    'final-price-two':                     { amount: get('discount-amount-two'),     type: 'amount'      },
    'final-price-precents-desktop':        { amount: get('discount-percents'),       type: 'percentage'  },
    'final-price-precents-desktop-two':    { amount: get('discount-percents-two'),   type: 'percentage'  },
    'final-price-precents-desktop-three':  { amount: get('discount-percents-three'), type: 'percentage'  },
    'final-price-precents-desktop-fourth': { amount: get('discount-percents-fourth'),type: 'percentage'  },
    'final-price-precents-desktop-fifth':  { amount: get('discount-percents-fifth'), type: 'percentage'  },
    'final-price-precents-desktop-sixth':  { amount: get('discount-percents-sixth'), type: 'percentage'  },
  };

  Object.entries(discounts).forEach(([id, cfg]) => {
    if (!cfg.amount) return;
    container.querySelectorAll('#' + id + ' .money').forEach(el => {
      el.textContent = formatFakeMoneyQV(calcDiscountedQV(basePrice, cfg.amount, cfg.type));
    });
  });
}

// ─── Initial application when the quick-view modal is first loaded ───────────
//
// `quickview:loaded` is dispatched by the QuickView class (theme.js) AFTER
// setInnerHTML has finished injecting the fetched section content.
// We add a minimal delay so any post-injection scripts can run first.

document.addEventListener('quickview:loaded', function () {
  setTimeout(function () {
    const container = getOpenModalContainer();
    if (container) applyFakePricesInModal(container);
  }, 50);
});

// ─── Re-apply after SSR variant change inside the modal ──────────────────────
//
// product-info inside the modal (same component as PDP) does SSR refresh and
// publishes variantChange AFTER updateSourceFromDestination replaces the
// Price block. We subscribe globally and scope to the open modal container.

(function subscribeToVariantChangeQV() {
  if (window.theme && window.theme.pubsub) {
    theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.variantChange, function () {
      const container = getOpenModalContainer();
      if (container) applyFakePricesInModal(container);
    });
  } else {
    // theme.js not yet ready (both are deferred, but retry just in case)
    setTimeout(subscribeToVariantChangeQV, 100);
  }
})();
 
